import * as React from "react";
import { MiniTooltipStep } from "@skbkontur/react-ui-tour";
import type { PopupPositionsType } from "@skbkontur/react-ui";
import { useEffect, useState } from "react";
import { Link } from "react-ui";
import ReactMarkdown from "react-markdown";

import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { MINITOUR_SHOW } from "../../metricsAction";
import { useCandyTheme } from "../../Internals/ThemeProvider/CandyTheme/UseCandyTheme";

export interface MinitourProps {
    id: string;
    label: string;
    children: React.ReactNode | readonly React.ReactNode[];
    width?: string | undefined;
    positions: PopupPositionsType[];
    isOpen?: boolean;
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    onClose?: () => void;
    openFromLink?: boolean;
    minitourEnabled?: boolean;
    openedFrom?: string;
}

interface MinitourContentProps {
    label: string;
    children: React.ReactNode;
    width?: string;
    positions: PopupPositionsType[];
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onClose: () => void;
    openedFrom?: string;
    minitourEnabled: boolean;
}

const MinitourContent: React.FC<MinitourContentProps> = ({
    label,
    children: minitourChildren,
    width,
    positions,
    isOpen,
    setIsOpen,
    onClose,
    openedFrom,
    minitourEnabled,
}) => {
    const ref = React.useRef<HTMLSpanElement>(null);
    const [isAnchorReady, setIsAnchorReady] = useState(false);

    useEffect(() => {
        if (ref.current) {
            setIsAnchorReady(true);
        }
    }, []);

    const isOpenMinitour = isAnchorReady && isOpen && minitourEnabled;

    const [metricSended, setMetricSended] = useState(false);
    const track = useMetrics();

    const onNext = function () {
        // Минитур может выдавать ошибки в консоль, поэтому добавлен пустой метод
        // Можно убрать когда решится issue: https://git.skbkontur.ru/ke/keweb.front.components/-/issues/1
    };

    useEffect(() => {
        if (isOpenMinitour && !metricSended) {
            setMetricSended(true);
            track(MINITOUR_SHOW, {
                from: openedFrom,
            });
        }
    }, [isOpenMinitour]);

    const color = useCandyTheme().invertedColors.color;
    const textNode = (
        <ReactMarkdown
            components={{
                p: ({ children }) => <span>{children}</span>,
                a: ({ href, children }) => (
                    <Link
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            color: color,
                            textDecoration: "underline",
                        }}>
                        {children}
                    </Link>
                ),
            }}>
            {label ?? ""}
        </ReactMarkdown>
    );

    const handleClickTargetAction = () => {
        if (isOpenMinitour) {
            setIsOpen(false);
            onClose();
        }
    };

    return (
        <>
            <span ref={ref} style={{ display: "inline-block" }} onClick={handleClickTargetAction}>
                {minitourChildren}
            </span>
            {isOpenMinitour && (
                <span>
                    <MiniTooltipStep
                        anchorElement={() => ref.current as HTMLElement}
                        render={() => textNode}
                        allowedPositions={positions}
                        onClose={() => {
                            setIsOpen(false);
                            onClose();
                        }}
                        onNext={onNext}
                        width={width}
                    />
                </span>
            )}
        </>
    );
};

export const Minitour: React.FC<MinitourProps> = props => {
    const { minitourEnabled, isOpen, setIsOpen, onClose, children, id, openedFrom, ...restProps } = props;

    const hasControlProps = !!(isOpen && setIsOpen && onClose);

    if (!minitourEnabled || !hasControlProps) {
        return <>{children}</>;
    }

    return (
        <MinitourContent
            {...restProps}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onClose={onClose}
            openedFrom={openedFrom ?? id}
            minitourEnabled={minitourEnabled}>
            {children}
        </MinitourContent>
    );
};
